import React from "react";
import { Badge, Button, Select, Space, Table, Tag, Typography } from "antd";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import {
  CalendarEventStatus,
  CalendarEventType,
  type CalendarEvent,
} from "@fitness-app/data-models/entities/CalendarEvent";

import { MeetingUrlField } from "~/modules/Trainee/TraineeProfile/TraineeFeatures/TraineeCalendar/components/MeetingUrlField";
import { useUpdateCalendarEvent } from "~/modules/Trainee/TraineeProfile/TraineeFeatures/TraineeCalendar/hooks/useUpdateCalendarEventStatus";
import { ClientTableColumn } from "./ClientTableColumn";

interface ScheduledEventsProps {
  events: CalendarEvent[] | undefined;
  isLoading: boolean;
}

export const ScheduledEvents = ({ events, isLoading }: ScheduledEventsProps): React.ReactElement => {
  const { t } = useTranslation("dashboard");
  const navigate = useNavigate();
  const {
    mutation: { mutate: updateEvent, isLoading: isUpdatingEvent },
  } = useUpdateCalendarEvent();

  const getEventTypeLabel = (type: CalendarEventType): string => {
    const typeMap = {
      [CalendarEventType.PERSONAL_TRAINING]: t("calendar.eventTypes.personalTraining"),
      [CalendarEventType.ONLINE_TRAINING]: t("calendar.eventTypes.onlineTraining"),
      [CalendarEventType.TRAINING_PLAN]: t("calendar.eventTypes.trainingPlan"),
      [CalendarEventType.STATIONARY_CONSULTATIONS]: t("calendar.eventTypes.stationaryConsultations"),
      [CalendarEventType.ONLINE_CONSULTATIONS]: t("calendar.eventTypes.onlineConsultations"),
      [CalendarEventType.DIET]: t("calendar.eventTypes.diet"),
      [CalendarEventType.PRODUCT]: t("calendar.eventTypes.product"),
      [CalendarEventType.ONLINE_COOPERATION]: t("calendar.eventTypes.onlineCooperation"),
      [CalendarEventType.OTHER]: t("calendar.eventTypes.other"),
    };

    return typeMap[type] || t("eventTypes.other");
  };

  const getEventStatusBadge = (status: CalendarEventStatus) => {
    const statusMap = {
      [CalendarEventStatus.ACTIVE]: (
        <Badge status="processing" text={t("dashboard:calendar.calendarEventStatus.active")} />
      ),
      [CalendarEventStatus.CANCELED]: (
        <Badge status="error" text={t("dashboard:calendar.calendarEventStatus.canceled")} />
      ),
      [CalendarEventStatus.NO_SHOW]: (
        <Badge status="warning" text={t("dashboard:calendar.calendarEventStatus.noShow")} />
      ),
      [CalendarEventStatus.ATTENDED]: (
        <Badge status="success" text={t("dashboard:calendar.calendarEventStatus.attended")} />
      ),
    };

    return statusMap[status] || <Badge status="default" text={t("status.unknown")} />;
  };

  return (
    <Table<CalendarEvent & { trainee?: { firstName: string | null; lastName: string | null } | null }>
      dataSource={events}
      rowKey="id"
      size="middle"
      scroll={{ x: true }}
      loading={isLoading}
      pagination={false}
      locale={{
        emptyText: "Brak zaplanowanych wydarzeń w tym tygodniu",
      }}
    >
      <Table.Column<CalendarEvent>
        title={t("calendar.tableHeader.date")}
        key="startTime"
        render={(_, record) => (
          <Typography.Text>{dayjs(record.startTime).locale("pl").format("DD MMM, ddd")}</Typography.Text>
        )}
        sorter={(a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime()}
        defaultSortOrder="ascend"
      />
      <Table.Column<CalendarEvent>
        title={t("calendar.tableHeader.time")}
        key="time"
        render={(_, record) => (
          <Typography.Text>
            {dayjs(record.startTime).format("HH:mm")} - {dayjs(record.endTime).format("HH:mm")}
          </Typography.Text>
        )}
      />
      <Table.Column<CalendarEvent & { trainee?: { firstName: string | null; lastName: string | null } | null }>
        title={t("calendar.tableHeader.client")}
        dataIndex="traineeId"
        key="traineeId"
        render={(traineeId: string | null, record) => <ClientTableColumn traineeId={traineeId} record={record} />}
      />
      <Table.Column<CalendarEvent>
        title={t("calendar.tableHeader.type")}
        dataIndex="type"
        key="type"
        render={(type: CalendarEventType) => <Tag color="blue">{getEventTypeLabel(type)}</Tag>}
        filters={Object.values(CalendarEventType).map((type) => ({
          text: getEventTypeLabel(type),
          value: type,
        }))}
        onFilter={(value, record) => record.type === value}
      />
      <Table.Column<CalendarEvent>
        title={t("calendar.tableHeader.status")}
        dataIndex="status"
        key="status"
        render={(status: CalendarEventStatus, record) => (
          <Select
            value={status}
            style={{ width: 160 }}
            loading={isUpdatingEvent && (updateEvent as any)?.variables?.eventId === record.id}
            onChange={(newStatus) => {
              updateEvent({
                eventId: record.id,
                update: { status: newStatus },
              });
            }}
            options={Object.values(CalendarEventStatus).map((s) => ({
              value: s,
              label: getEventStatusBadge(s),
            }))}
          />
        )}
        filters={Object.values(CalendarEventStatus).map((status) => ({
          text: t(`calendar.calendarEventStatus.${status.toLowerCase()}`),
          value: status,
        }))}
        onFilter={(value, record) => record.status === value}
      />
      <Table.Column<CalendarEvent>
        title={t("calendar.tableHeader.meetingUrl")}
        key="meetingUrl"
        render={(_, record) => <MeetingUrlField event={record} source="scheduled_events" />}
      />
      <Table.Column<CalendarEvent>
        title={t("calendar.tableHeader.actions")}
        key="actions"
        render={(_, record) => (
          <Space>
            <Button type="link" onClick={() => navigate(`/trainee/details/${record.traineeId}`)}>
              {t("common:button.details")}
            </Button>
          </Space>
        )}
      />
    </Table>
  );
};
